import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import CarouselModal from '../../../../components/globals/CarouselModal';
import GlobalTheme from '../../../../styled/GlobalTheme';
import BodySlider from './BodySlider';
import HeaderSlider from './HeaderSlider';
import { piper, tutorialModal } from '../../../../assets/illustrations/illustrationRepository';
import useCallGA from '../../../../hooks/useCallGA';

const Slider = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme, color }) => (theme.colors[color])};
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 150px 1fr 58px;
  ${GlobalTheme.breakpoints.down('sm')} {
    grid-template-rows: auto 1fr 58px;
  }
  @media (max-height: 425px) {
    grid-template-rows: 1fr 2fr 58px;
  }
`;

const ContainerButton = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0 20px;
  ${GlobalTheme.breakpoints.down('sm')} {
    padding: 0 11px;
  }
`;
const SkipButton = styled.div`
  cursor: pointer;
  color: white;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  width: fit-content;
  pointer-events: auto;
  ${GlobalTheme.breakpoints.down('sm')} {
    font-size: 16px;
  }
`;

const TutorialModal = () => {
  const [openModal, setOpenModal] = useState(true);
  const intl = useIntl();

  const getImageNameByLanguage = (sliderName) => {
    if (intl.locale === 'es') {
      return tutorialModal[`${sliderName}Es`];
    }
    return tutorialModal[`${sliderName}En`];
  };

  const callGA = useCallGA();

  const sliders = [
    {
      color: 'lightPurple',
      header: {
        title: intl.formatMessage({
          id: 'tutorialModal.createapp.slide1.title',
          defaultMessage: 'Welcome',
        }),
        text: intl.formatMessage({
          id: 'tutorialModal.slide1.text',
          defaultMessage: 'Check your Piper Score and see how your business is doing in your Dashboard.',
        }),
        hasImage: false,
      },
      body: {
        images: {
          lg: getImageNameByLanguage('sliderOne'),
          md: getImageNameByLanguage('sliderOneMd'),
          sm: getImageNameByLanguage('sliderOneSm'),
        },
        hasPiper: true,
      },
    },
    {
      color: 'lightBlue',
      header: {
        image: piper.Camera,
        text: intl.formatMessage({
          id: 'tutorialModal.createapp.slide2.text',
          defaultMessage: 'Check the Creative Center and choose from my recommendations or from our hundreds of ready-to-use templates',
        }),
        hasImage: true,
      },
      body: {
        images: {
          lg: getImageNameByLanguage('sliderTwo'),
          md: getImageNameByLanguage('sliderTwo'),
          sm: getImageNameByLanguage('sliderTwoSm'),
        },
        hasPiper: false,
      },
    },
    {
      color: 'green',
      header: {
        image: piper.Calendar,
        text: intl.formatMessage({
          id: 'tutorialModal.createapp.slide3.text',
          defaultMessage: 'Get my Good time to post recommendations to publish your content, schedule it and organize your content and Ads',
        }),
        hasImage: true,
      },
      body: {
        images: {
          lg: getImageNameByLanguage('sliderTree'),
          md: getImageNameByLanguage('sliderTree'),
          sm: getImageNameByLanguage('sliderTreeSm'),
        },
        hasPiper: false,
      },
    },
    {
      color: 'brand5',
      header: {
        image: piper.Rocket,
        text: intl.formatMessage({
          id: 'tutorialModal.createapp.slide4.text',
          defaultMessage: 'Check at a glance how are your Social Ads performing and manage your advertising Budget',
        }),
        hasImage: true,
      },
      body: {
        images: {
          lg: getImageNameByLanguage('sliderFour'),
          md: getImageNameByLanguage('sliderFour'),
          sm: getImageNameByLanguage('sliderFourSm'),
        },
        hasPiper: false,
      },
    },
    {
      color: 'orange',
      header: {
        image: piper.PiperMessagesNoBackground,
        text: intl.formatMessage({
          id: 'tutorialModal.createapp.slide5.text',
          defaultMessage: 'Interact with your social media users in a single place by connecting your social profiles',
        }),
        hasImage: true,
      },
      body: {
        images: {
          lg: getImageNameByLanguage('sliderFive'),
          md: getImageNameByLanguage('sliderFive'),
          sm: getImageNameByLanguage('sliderFiveSm'),
        },
        hasPiper: false,
      },
    },
  ];

  let slideCounter = 1;
  const limit = sliders.length;
  const [skipped, setSkipped] = useState(true);

  useEffect(() => {
    const execute = () => {
      setTimeout(() => {
        const nextButton = document.querySelector('[aria-label="Next"]');
        nextButton.addEventListener('click', () => {
          slideCounter = (slideCounter < limit) ? (slideCounter + 1) : slideCounter;
          if (slideCounter === limit) {
            setSkipped(false);
          }
          callGA({
            action: 'Press Next',
            label: '(button)',
          });
        });
        const previousButton = document.querySelector('[aria-label="Previous"]');
        previousButton.addEventListener('click', () => {
          slideCounter = (slideCounter > 1) ? (slideCounter - 1) : slideCounter;
          callGA({
            action: 'Press Previous',
            label: '(button)',
          });
        });
      }, 500);
    };
    execute();
  }, []);

  const handleClose = () => {
    const action = (skipped) ? 'Tutorial Skipped' : 'Tutorial Done';
    setOpenModal(false);
    callGA({ action, label: '(button)' });
  };

  return (
    <CarouselModal
      open={openModal}
      sliders={
        sliders.map((slider, index) => (
          <Slider color={slider.color}>
            <HeaderSlider
              image={slider.header.image || ''}
              title={slider.header.title || ''}
              text={slider.header.text}
              hasImage={slider.header.hasImage}
            />
            <BodySlider
              images={slider.body.images}
              hasPiperTip={slider.body.hasPiper}
            />
            <ContainerButton>
              <SkipButton onClick={() => handleClose()}>
                {sliders.length - 1 === index ? (
                  <FormattedMessage
                    id="tutorialModal.done"
                    defaultMessage="Done"
                  />
                ) : (
                  <FormattedMessage
                    id="tutorialModal.skip"
                    defaultMessage="Skip"
                  />
                )}
              </SkipButton>
            </ContainerButton>
          </Slider>
        ))
      }
    />
  );
};

export default TutorialModal;
